/* eslint-disable react/prop-types */
import { useRef, useEffect, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Dropdown } from 'antd';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { listDotPhraseOverrides } from 'src/modules/ambient_solo/store/actions/settings';
import CopySection from '../../modules/ambient_solo/components/baseComponents/CopySection';
import { useTenant } from 'src/hooks/misc';

const Editor = ({
  value,
  onChange,
  className,
  disabled,
  enableDotPhrases = false,
}) => {
  const quillRef = useRef(null);
  const divRef = useRef(null);
  const cursorPositionRef = useRef(null);
  const selectedTextRef = useRef('');
  const copySectionRef = useRef(null);
  const [hoveredElement, setHoveredElement] = useState(null);
  const [copiedSectionText, setCopiedSectionText] = useState('');

  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ x: 0, y: 0 });

  const [showCopyTooltip, setShowCopyTooltip] = useState(false);
  const [copyTooltipPosition, setCopyTooltipPosition] = useState({
    x: 0,
    y: 0,
  });
  const [menu, setMenu] = useState(<></>);

  const dispatch = useDispatch();

  const { dotPhraseOverridesList } = useSelector(
    (state) => state?.settingsState
  );

  useEffect(() => {
    if (enableDotPhrases) {
      dispatch(listDotPhraseOverrides());
    }
  }, []);

  useEffect(() => {
    const quill = quillRef?.current?.getEditor();
    if (quill) {
      const editorElement = quill.root;
      if (editorElement) {
        editorElement.setAttribute('spellcheck', 'true');
        editorElement.setAttribute('contenteditable', 'true');
      }
    }
  }, [quillRef]);

  useEffect(() => {
    let menuItems = [
      {
        label: 'Copy',
        key: 'copy',
        onClick: copyToClipboard,
      },
      {
        label: 'Paste',
        key: 'paste',
        onClick: insertFromClipboard,
      },
    ];

    const submenuItems = dotPhraseOverridesList.map((dotPhrase, index) => ({
      label: dotPhrase.title,
      key: `sub-${dotPhrase.title} ${index}`,
      onClick: () => insertText(dotPhrase.template),
    }));

    if (dotPhraseOverridesList.length > 0) {
      submenuItems.push({
        key: 'divider1',
        type: 'divider',
      });
    }

    submenuItems.push({
      label: 'Create a new dot phrase',
      key: 'create-dot-phrase',
      onClick: () => window.open('/setting', '_blank'),
    });

    menuItems.push({
      label: 'Insert dot phrase',
      key: 'dot-phrases',
      children: submenuItems,
    });
    menuItems.push({
      key: 'spell-divider',
      type: 'divider',
    });

    // Add usage hint
    menuItems.push({
      label: (
        <div className="text-gray-500 text-xs py-1">
          💡 {navigator.platform.includes('Mac') ? 'Cmd' : 'Ctrl'}+Right-click
          on misspelled words for suggestions
        </div>
      ),
      key: 'spell-hint',
      disabled: true,
    });

    setMenu({ items: menuItems });
  }, [dotPhraseOverridesList]);

  const tenant = useTenant();

  const handleMouseOver = (event) => {
    const target = event.target;

    // Check if the clicked element is a <strong> tag
    if (target.tagName === 'STRONG') {
      setShowCopyTooltip(true);
      setCopyTooltipPosition({ x: event.pageX, y: event.pageY });
      setHoveredElement(target);

      // getting the complete section Text and storing it in state
      const currentText = target.outerHTML;
      const currentPosInValue = value?.indexOf(currentText);
      let endingPosition = value?.indexOf('<p><strong', currentPosInValue - 2);

      if (endingPosition === -1) {
        endingPosition = value?.length - 4;
      }
      const sectionTextToCopy = value?.substring(
        currentPosInValue,
        endingPosition
      );
      setCopiedSectionText(sectionTextToCopy);
    }
  };

  const handleMouseOut = (event) => {
    const target = event.target;
    const copySection = copySectionRef?.current;
    if (
      hoveredElement &&
      target.tagName === 'STRONG' &&
      (!copySection || !hoveredElement.contains(copySection))
    ) {
      setShowCopyTooltip(false);
    }
  };

  const handleTooltipMouseOver = () => {
    // Keep the tooltip visible when mouse enters the tooltip itself
    setShowCopyTooltip(true);
  };

  const handleTooltipMouseOut = (event) => {
    const relatedTarget = event.relatedTarget;

    // Hide the tooltip only if the mouse leaves both the tooltip and the <strong> element
    if (!relatedTarget || !hoveredElement.contains(relatedTarget)) {
      setShowCopyTooltip(false);
    }
  };

  const insertText = (template) => {
    const editor = quillRef.current.getEditor();
    let position = cursorPositionRef.current;
    if (!position) {
      position = editor.getLength(); // insert at the end if no cursor position
    }

    // Capture the current scroll position
    const scrollPosition = editor.root.scrollTop;

    editor.insertText(position, template);
    const newPosition = position + template.length;
    editor.setSelection(newPosition, 0);

    // Restore the scroll position
    setTimeout(() => {
      editor.root.scrollTop = scrollPosition;
    }, 0);

    setShowDropdown(false);
  };

  const handleRightClick = (event) => {
    const editor = quillRef.current.getEditor();
    const selection = editor.getSelection();
    const currentCursorPosition = editor.getSelection()?.index;
    cursorPositionRef.current = currentCursorPosition;

    // Check if Ctrl (or Cmd on Mac) is pressed
    const showNativeSpellCheck = event.ctrlKey || event.metaKey;

    if (showNativeSpellCheck) {
      // Allow native browser spell check when Ctrl/Cmd+Right-click
      // Don't prevent default - let browser handle spell check suggestions
      return;
    } else {
      // Show custom editor menu on regular right-click
      event.preventDefault();

      if (selection) {
        const text = editor.getText(selection.index, selection.length);
        selectedTextRef.current = text; // Store the selected text
      } else {
        selectedTextRef.current = ''; // Reset if no selection
      }
      setShowDropdown(true);
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const dropdownWidth = 350;
      const dropdownHeight = 200;

      let x = event.pageX;
      let y = event.pageY;

      if (x + dropdownWidth > viewportWidth) {
        x = viewportWidth - dropdownWidth - 10;
      }

      if (y + dropdownHeight > viewportHeight) {
        y = viewportHeight - dropdownHeight - 20;
      }

      setDropdownPosition({ x, y });
      cursorPositionRef.current = editor.getSelection()?.index;
    }
  };

  const insertFromClipboard = () => {
    navigator.clipboard
      .readText()
      .then((clipText) => {
        insertText(clipText); // Reuse the insertText function
      })
      .catch((err) => {
        console.error('Failed to read from clipboard', err);
      });
  };

  const copyToClipboard = () => {
    const text = selectedTextRef.current;
    if (text) {
      navigator.clipboard
        .writeText(text.trim())
        .then(() => {})
        .catch((err) => {
          console.error('Failed to copy text to clipboard', err);
        });
    }
  };

  useEffect(() => {
    if (enableDotPhrases) {
      const editor = divRef.current;
      if (editor) {
        editor.addEventListener('contextmenu', handleRightClick);
        editor.addEventListener('mouseover', handleMouseOver);
        editor.addEventListener('mouseout', handleMouseOut);
      }

      return () => {
        if (editor) {
          editor.removeEventListener('contextmenu', handleRightClick);
          editor.removeEventListener('mouseover', handleMouseOver);
          editor.removeEventListener('mouseout', handleMouseOut);
        }
      };
    }
  }, [hoveredElement, value]);

  return (
    <div ref={divRef}>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        modules={{ toolbar: [] }}
        formats={[
          'background',
          'bold',
          'color',
          'font',
          'code',
          'italic',
          'link',
          'size',
          'strike',
          'script',
          'blockquote',
          'header',
          'indent',
          'align',
          'direction',
          'code-block',
          'formula',
          'image',
          'video',
        ]}
        className={className}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
      {enableDotPhrases && showDropdown && (
        <Dropdown
          menu={menu}
          open={showDropdown}
          onOpenChange={(visible) => setShowDropdown(visible)}
          trigger={['contextMenu']}
          overlayClassName="custom-dropdown"
          getPopupContainer={() => document.body}
          overlayStyle={{
            position: 'fixed',
            left: `${dropdownPosition.x}px`,
            top: `${dropdownPosition.y}px`,
          }}
        >
          <div></div>
        </Dropdown>
      )}
      <CopySection
        showCopyTooltip={showCopyTooltip}
        copyTooltipPosition={copyTooltipPosition}
        onMouseOver={handleTooltipMouseOver}
        onMouseLeave={handleTooltipMouseOut}
        copiedSectionText={copiedSectionText}
        copySectionRef={copySectionRef}
        copySectionTitle={!['OP'].includes(tenant)}
      />
    </div>
  );
};

export default Editor;
